import React from 'react';
import OsScroll from './ui/OsScroll';
import Tooltip from './ui/Tooltip';
import { Boxes, ExternalLink, Folder, Save } from 'lucide-react';
import { httpMethodLabelClass as methodLabelClass } from '../utils/httpMethodLabelClass';

const TYPE_LABELS = {
  workspace: 'Workspace',
  collection: 'Collection',
  request: 'Request',
};

/** A request line styled exactly like a Collections-sidebar request row. */
export function ShareRequestRow({ method, name }) {
  return (
    <div className="chat-share-req-row">
      <span
        className={`sidebar-request-method shrink-0 font-semibold uppercase tracking-wide mr-1 ${methodLabelClass(method)}`}
      >
        {(method || 'GET').toUpperCase()}
      </span>
      <span className="chat-share-req-name">{name || 'Untitled'}</span>
    </div>
  );
}

/**
 * A `kind: 'share'` chat message rendered the way the shared item looks in the
 * Collections sidebar: a request is a method+name row; a collection is a
 * folder row with its request rows nested beneath it.
 *
 * Request cards have two actions: Play (`onPlay`) opens the request straight
 * into the builder as an unsaved tab, Save (`onOpen`) opens the fork prompt
 * with the destination-collection picker. Workspace/collection cards have a
 * single action — the fork prompt.
 */
export default function ShareMessageCard({ metadata, onOpen, onPlay }) {
  const meta = metadata || {};
  const type = meta.share_type;
  const isRequest = type === 'request';
  const previewRequests = Array.isArray(meta.requests) ? meta.requests : [];
  const hiddenCount =
    meta.item_count != null ? Math.max(0, meta.item_count - previewRequests.length) : 0;

  const subParts = [TYPE_LABELS[type] || 'Share'];
  if (type === 'collection' && meta.item_count != null) {
    subParts.push(`${meta.item_count} request${meta.item_count === 1 ? '' : 's'}`);
  }
  if (type === 'workspace' && meta.item_count != null) {
    subParts.push(`${meta.item_count} collection${meta.item_count === 1 ? '' : 's'}`);
  }
  if (meta.workspace_name) subParts.push(meta.workspace_name);
  if (meta.snapshot) subParts.push('snapshot');

  // Card click mirrors the primary action: try the request now, or fork.
  const primary = () => (isRequest && onPlay ? onPlay(meta) : onOpen(meta));

  return (
    <div
      className="chat-share-card"
      role="button"
      tabIndex={0}
      onClick={primary}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          primary();
        }
      }}
    >
      <div className="chat-share-card-head">
        {isRequest ? (
          <ShareRequestRow method={meta.method} name={meta.label} />
        ) : (
          <div className="chat-share-req-row">
            {type === 'workspace' ? (
              <Boxes size={14} className="chat-share-card-typeicon" />
            ) : (
              <Folder size={14} className="chat-share-card-typeicon" />
            )}
            <span className="chat-share-card-name">{meta.label || 'Shared item'}</span>
          </div>
        )}
        <div className="chat-share-card-actions">
          {/* The app's own Tooltip, never the native `title`: the OS bubble
              ignores the theme and arrives on its own slow timer, which is why
              every other hint in the app goes through this component. */}
          {isRequest && (
            <Tooltip content="Save to a collection…" side="top">
              <button
                className="chat-share-card-save"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(meta);
                }}
              >
                <Save size={13} />
              </button>
            </Tooltip>
          )}
          <Tooltip
            content={isRequest ? 'Open in request builder' : 'Save to a workspace…'}
            side="top"
          >
            <button
              className="chat-share-card-play"
              onClick={(e) => {
                e.stopPropagation();
                primary();
              }}
            >
              {/* Requests open straight into the builder; everything else forks/saves. */}
              {isRequest ? <ExternalLink size={13} /> : <Save size={13} />}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* OsScroll, not `overflow-y: auto`: macOS WKWebView swaps a styled
          ::-webkit-scrollbar for the wide native bar as soon as the pointer
          reaches it, and inside a chat bubble that bar covers the request
          names it is scrolling. */}
      {type === 'collection' && previewRequests.length > 0 && (
        <OsScroll className="chat-share-card-tree" defer>
          {previewRequests.map((r, i) => (
            <ShareRequestRow key={i} method={r.method} name={r.name} />
          ))}
          {hiddenCount > 0 && (
            <div className="chat-share-card-more">+{hiddenCount} more</div>
          )}
        </OsScroll>
      )}

      <div className="chat-share-card-sub">{subParts.join(' · ')}</div>
    </div>
  );
}
